
import React from "react";
import Layout from "@/components/Layout";
import { useLocation, Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { ChevronLeft } from "lucide-react";

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
  backLink?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title, backLink }) => {
  const location = useLocation();
  
  // Function to generate breadcrumbs based on current path
  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    const breadcrumbs = [];
    
    // Always start with Dashboard
    breadcrumbs.push({
      name: "Admin Dashboard",
      path: "/admin/dashboard",
      current: pathnames.length === 2 && pathnames[1] === "dashboard"
    });
    
    // Add section (e.g., courses, users, certificates)
    if (pathnames.length > 2) {
      const section = pathnames[2];
      let sectionName = "";
      
      switch (section) {
        case "courses":
          sectionName = "Course Management";
          break;
        case "users":
          sectionName = "User Management";
          break;
        case "certificates":
          sectionName = "Certificate Management";
          break;
        default:
          sectionName = section.charAt(0).toUpperCase() + section.slice(1);
      }
      
      breadcrumbs.push({
        name: sectionName,
        path: `/admin/${section}`,
        current: pathnames.length === 3
      });
    }
    
    // Add subsection (e.g., modules, templates)
    if (pathnames.length > 3) {
      const subsection = pathnames[3];
      let subsectionName = "";
      let path = "";
      
      if (subsection === "modules") {
        if (pathnames.length > 4 && pathnames[4] === "lessons") {
          // This is for the module lessons page
          const moduleId = pathnames[5];
          subsectionName = "Modules";
          path = `/admin/courses/modules/${pathnames[4]}`;
          
          breadcrumbs.push({
            name: subsectionName,
            path: `/admin/courses/modules/${pathnames[4]}`,
            current: false
          });
          
          // Add module lessons breadcrumb
          breadcrumbs.push({
            name: "Lessons",
            path: `/admin/courses/modules/lessons/${moduleId}`,
            current: true
          });
          
          return breadcrumbs;
        } else {
          const courseId = pathnames[4];
          subsectionName = "Modules";
          path = `/admin/courses/modules/${courseId}`;
        }
      } else if (subsection === "lessons") {
        const lessonId = pathnames[4];
        subsectionName = "Lesson";
        path = `/admin/courses/lessons/${lessonId}`;
      } else if (subsection === "templates") {
        subsectionName = "Templates";
        path = "/admin/certificates/templates";
      } else if (subsection === "edit") {
        const entityId = pathnames[4];
        subsectionName = "Edit";
        path = `/admin/${pathnames[2]}/edit/${entityId}`;
      } else {
        subsectionName = subsection.charAt(0).toUpperCase() + subsection.slice(1);
        path = `/admin/${pathnames[2]}/${subsection}`;
      }
      
      breadcrumbs.push({
        name: subsectionName,
        path: path,
        current: pathnames.length === 4 || (pathnames.length === 5 && !["edit", "new"].includes(pathnames[4]))
      });
    }
    
    // Add action (e.g., edit, new)
    if (pathnames.length > 4) {
      const lastSegment = pathnames[4];
      if (lastSegment === "edit" || lastSegment === "new") {
        breadcrumbs.push({
          name: lastSegment === "new" ? "Create" : "Edit",
          path: location.pathname,
          current: true
        });
      }
    }
    
    return breadcrumbs;
  };
  
  const breadcrumbs = generateBreadcrumbs();
  
  return (
    <Layout>
      <div className="container mx-auto py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {backLink && (
              <Link to={backLink} className="flex items-center text-gray-600 hover:text-gray-900 mb-2">
                <ChevronLeft className="h-4 w-4 mr-1" />
                <span>Back</span>
              </Link>
            )}
            <h1 className="text-3xl font-bold">{title || "Admin"}</h1>
          </div>
          
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  <BreadcrumbItem>
                    {crumb.current ? (
                      <BreadcrumbPage>{crumb.name}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.path}>
                          {crumb.name}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        {children}
      </div>
    </Layout>
  );
};

export default AdminLayout;
